import { FunctionComponent, useEffect } from 'react';
import styles from "./ResetPassword.module.css";
import {Image} from 'react-bootstrap';
import cn from 'classnames';
import AuthLayout from '../../../component/common/Layout/AuthLayout/AuthLayout';
import FormField, { IFieldType } from '../../../component/common/FormField/FormField';
import { PageRoutes } from '../../../routing/paths';
import { useLocation, useNavigate } from "react-router-dom";
import { ASSESTS } from '../../../constant/assest';
import { useDispatch, useSelector } from 'react-redux';
import { resetPassword, resetSubAdminPassword } from '../../../redux/slices/auth/actions';
import { clearMessage, clearError } from '../../../redux/slices/auth/slice';
import { AppDispatch, RootState } from '../../../redux/store';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import { resetPasswordSchema } from '../../../validation/authValidation';

const ResetPassword: FunctionComponent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const { loading, error, message, tempPassword } = useSelector((state: RootState) => state.auth);

    const subAdminResetState = location.state as { userId: string; userName: string } | undefined;
    const isSubAdminReset = !!subAdminResetState?.userId;

    const formik = useFormik({
        initialValues: {
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: resetPasswordSchema,
        onSubmit: values => {            
            if (isSubAdminReset) {
                // This will be called when superAdmin resets the password of subAdmin after clicking on the accept button in the Notification received
                dispatch(resetSubAdminPassword({ id: Number(subAdminResetState.userId), password: values.newPassword }));
            } else {
                if (!tempPassword) {
                    toast.error('Temporary password not found. Please try the forgot password process again.');
                    return;
                }
                dispatch(resetPassword({ currentPassword: tempPassword, newPassword: values.newPassword }));
            }
        },
    });

    const handleBlurWithTrim = (e: React.FocusEvent<HTMLInputElement>) => {
        formik.handleBlur(e); // for touched state
        const { name, value } = e.target;
        const trimmedValue = value.trim();

        if (value !== trimmedValue) {
            // Create a fake change event to update the value via formik.handleChange
            const fakeChangeEvent = {
                target: {
                    name: name,
                    value: trimmedValue,
                },
            };
            formik.handleChange(fakeChangeEvent);
        }
    };

    useEffect(() => {
        if (message) {
            toast.success(message);
            dispatch(clearMessage());
            if (isSubAdminReset) {
                navigate(-1);
            } else {
                navigate(PageRoutes.DASHBOARD);
            }
        }
    }, [message, navigate, dispatch, isSubAdminReset]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const title = isSubAdminReset ? `Create password for ${subAdminResetState.userName}` : 'Reset Password';
    const buttonText = isSubAdminReset ? 'Create Password' : 'Reset Password';
    const buttonLoadingText = isSubAdminReset ? 'Creating...' : 'Resetting...';

    return (
        <AuthLayout>
            <div className={styles.logo}>
                <Image 
                    src={ASSESTS.LOGO}
                    width={291}
                    height={73}
                    alt="Fishinity"
                />
            </div>
            <div className={styles.header}>{title}</div>
            <form onSubmit={formik.handleSubmit} className={styles.form}>
                <FormField 
                    label='Password'
                    placeholder='Enter Password'
                    fieldType={IFieldType.PASSWORD}
                    className='mb-4'
                    showEyeIcon={true}
                    name="newPassword"
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    onBlur={handleBlurWithTrim}
                    errorText={formik.errors.newPassword}
                    showError={!!(formik.touched.newPassword && formik.errors.newPassword)}
                />
                <FormField 
                    label='Confirm Password'
                    placeholder='Confirm Password'
                    fieldType={IFieldType.PASSWORD}
                    className='mb-4'
                    showEyeIcon={true}
                    name="confirmPassword"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={handleBlurWithTrim}
                    errorText={formik.errors.confirmPassword}
                    showError={!!(formik.touched.confirmPassword && formik.errors.confirmPassword)}
                />
                <div className={cn(styles.submit, styles.mb4)}>
                    <button 
                        type="submit"
                        className='customBtn greenBtn'
                        disabled={loading === 'pending'}
                    >
                        {loading === 'pending' ? buttonLoadingText : buttonText}
                    </button>
                </div>
            </form>
        </AuthLayout>
    );
};

export default ResetPassword;